/**
 * 华为应用商店 → 红线规则配置（CLAUDE.md §前端 UI 须借鉴 LEO v1.0；本页复用 leo-* 类）。
 *
 * 行为：
 *   - 进入页面：GET /api/v1/huawei/store-redline-config 拉取全部配置行。
 *   - 按 field → parent_id → child_id 三级分组渲染为 4 个区块卡片；field 展示顺序
 *     按 FIELD_ORDER（投放时间/出价/监测链接/回传异常监控），未知 field 兜底排后。
 *   - 编辑器按 (field, parent_id, child_id) 规则分发：
 *       · 投放时间                  → 48 半小时色块拖拽选择器 + 豁免任务ID chips（LaunchTimeRangeEditor）
 *       · 出价                      → 数字输入 + 后缀「元」
 *       · 监测链接                  → 整宽多行完整展示（长 URL 逐字符换行，可编辑）
 *       · 回传异常监控/任务消耗超过  → 数字输入 + 后缀「元」（child_id 已在库内
 *         改名，存量环境经 consume_child_rename.sql 对齐）
 *       · 回传异常监控/转化链异常    → 双下拉（回传大于0指标 / 回传等于0指标），
 *         6 个下拉一律前端只读（disabled），值仅经库/后端维护
 *       · 其余未知行                → 文本输入兜底
 *   - 保存：PUT /api/v1/huawei/store-redline-config，body = { rows }（全量行）。
 *     create_user 保留首次、modify_user 每次更新（后端 SQL 层保证）。
 *   - 空表：显示「初始化默认配置」按钮 → POST /seed 种入 20 行初版配置。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type StoreRedlineConfigItem,
  type StoreRedlineConfigRecord,
  getStoreRedlineConfig,
  saveStoreRedlineConfig,
  seedStoreRedlineConfig,
} from "../../services/huawei/storeRedlineConfig";
import { LaunchTimeRangeEditor } from "../../components/huaweiRedlineConfig/LaunchTimeRangeEditor";
import { ConversionChainEditor } from "../../components/huaweiRedlineConfig/ConversionChainEditor";
import { pushToast } from "../../hooks/useToast";
import { useAuth } from "../../contexts/AuthContext";
import { PAGE_KEYS } from "../../config/permissions";

/** field 常量（与后端 store_redline_config_service 校验规则一致）。 */
const FIELD_LAUNCH_TIME = "投放时间";
const FIELD_BID = "出价";
const FIELD_MONITOR_URL = "监测链接";
const FIELD_CALLBACK_MONITOR = "回传异常监控";
const PARENT_CONVERSION_CHAIN = "转化链异常";
const CHILD_CONSUME_OVER = "任务消耗超过";

/** field 区块展示顺序（未知 field 兜底排后，按 DB 顺序）。 */
const FIELD_ORDER: string[] = [
  FIELD_LAUNCH_TIME,
  FIELD_BID,
  FIELD_MONITOR_URL,
  FIELD_CALLBACK_MONITOR,
];

/** 行签名（脏检查用）：三元组 + ope_value。 */
function sig(it: StoreRedlineConfigItem): string {
  return `${it.field}|${it.parent_id}|${it.child_id}|${it.ope_value}`;
}

/** 含审计列的记录 → 可编辑行。 */
function toItem(r: StoreRedlineConfigRecord): StoreRedlineConfigItem {
  return {
    field: r.field,
    parent_id: r.parent_id,
    child_id: r.child_id,
    ope_value: r.ope_value ?? "",
  };
}

/** 该行编辑器是否为「宽编辑器」（独占一行）：投放时间色块条 / 监测链接长 URL / 转化链双下拉。 */
function isWideEditor(row: StoreRedlineConfigItem): boolean {
  return (
    row.field === FIELD_LAUNCH_TIME ||
    row.field === FIELD_MONITOR_URL ||
    (row.field === FIELD_CALLBACK_MONITOR && row.parent_id === PARENT_CONVERSION_CHAIN)
  );
}

/** 行单元 key（与表主键一致）。 */
function rowKey(row: StoreRedlineConfigItem): string {
  return `${row.field}|${row.parent_id}|${row.child_id}`;
}

interface ParentGroup {
  parentId: string;
  rows: { row: StoreRedlineConfigItem; index: number }[];
}

interface FieldGroup {
  field: string;
  parents: ParentGroup[];
  /** 该区块内最近修改记录（按 updated_time 最大），用于区块右上角审计标签。 */
  latest: StoreRedlineConfigRecord | null;
}

export default function RedlineConfigPage() {
  const { canEdit } = useAuth();
  const canEditPage = canEdit(PAGE_KEYS.HUAWEI_REDLINE);
  const [items, setItems] = useState<StoreRedlineConfigItem[]>([]);
  const [loaded, setLoaded] = useState<StoreRedlineConfigRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const initialRef = useRef(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getStoreRedlineConfig();
      setItems(rows.map(toItem));
      setLoaded(rows);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "拉取华为商店红线配置失败";
      pushToast(msg, "error");
      setItems([]);
      setLoaded([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (initialRef.current) {
      initialRef.current = false;
    }
  }, [items]);

  const dirty = useMemo(() => {
    if (!loaded) return false;
    return items.map(sig).join("\n") !== loaded.map(sig).join("\n");
  }, [items, loaded]);

  function updateValue(index: number, ope_value: string) {
    setItems((prev) => prev.map((it, j) => (j === index ? { ...it, ope_value } : it)));
  }

  async function onSeed() {
    setSeeding(true);
    try {
      const n = await seedStoreRedlineConfig();
      pushToast(
        n > 0 ? `已初始化 ${n} 行初版配置` : "配置已存在，未覆盖（仅空表生效）",
        n > 0 ? "success" : "info",
      );
      await reload();
    } catch (err) {
      pushToast(err instanceof ApiError ? err.message : "初始化失败", "error");
    } finally {
      setSeeding(false);
    }
  }

  async function onSave() {
    setSaving(true);
    try {
      const rows = await saveStoreRedlineConfig({ rows: items });
      setItems(rows.map(toItem));
      setLoaded(rows);
      pushToast("保存成功", "success");
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "保存失败",
        "error",
      );
    } finally {
      setSaving(false);
    }
  }

  /** 按 field → parent_id 分组（组内保持 DB 排序），field 按 FIELD_ORDER 排。 */
  const groups: FieldGroup[] = useMemo(() => {
    if (!loaded) return [];
    const recordByKey = new Map(loaded.map((r) => [rowKey(r), r]));
    const fieldMap = new Map<string, Map<string, { row: StoreRedlineConfigItem; index: number }[]>>();
    items.forEach((row, index) => {
      let parentMap = fieldMap.get(row.field);
      if (!parentMap) {
        parentMap = new Map();
        fieldMap.set(row.field, parentMap);
      }
      let rows = parentMap.get(row.parent_id);
      if (!rows) {
        rows = [];
        parentMap.set(row.parent_id, rows);
      }
      rows.push({ row, index });
    });
    const fieldNames = [...fieldMap.keys()].sort((a, b) => {
      const ia = FIELD_ORDER.indexOf(a);
      const ib = FIELD_ORDER.indexOf(b);
      return (ia === -1 ? FIELD_ORDER.length : ia) - (ib === -1 ? FIELD_ORDER.length : ib);
    });
    return fieldNames.map((field) => {
      const parents: ParentGroup[] = [...(fieldMap.get(field) ?? [])].map(([parentId, rows]) => ({
        parentId,
        rows,
      }));
      const sectionRecords = parents
        .flatMap((p) => p.rows.map((r) => recordByKey.get(rowKey(r.row))))
        .filter((r): r is StoreRedlineConfigRecord => r !== undefined);
      const latest = sectionRecords.reduce<StoreRedlineConfigRecord | null>(
        (acc, r) => (!acc || (r.updated_time ?? "") > (acc.updated_time ?? "") ? r : acc),
        null,
      );
      return { field, parents, latest };
    });
  }, [items, loaded]);

  /** 按 (field, parent_id, child_id) 规则分发编辑器；未知行文本输入兜底。 */
  function renderEditor(row: StoreRedlineConfigItem, index: number, editable: boolean) {
    const onValue = (v: string) => updateValue(index, v);

    if (row.field === FIELD_LAUNCH_TIME) {
      return (
        <LaunchTimeRangeEditor value={row.ope_value} onChange={onValue} disabled={!editable} />
      );
    }
    if (row.field === FIELD_CALLBACK_MONITOR && row.parent_id === PARENT_CONVERSION_CHAIN) {
      // 转化链异常：6 个下拉一律前端只读（有编辑权限也不可改），值仅经库/后端维护
      return (
        <ConversionChainEditor value={row.ope_value} onChange={onValue} disabled />
      );
    }
    if (row.field === FIELD_BID) {
      return (
        <span className="flex items-center gap-1.5">
          <input
            type="number"
            className="leo-input w-32"
            value={row.ope_value}
            readOnly={!editable}
            onChange={(e) => onValue(e.target.value)}
          />
          <span className="text-[12px] text-text-muted">元</span>
        </span>
      );
    }
    if (row.field === FIELD_CALLBACK_MONITOR && row.child_id === CHILD_CONSUME_OVER) {
      return (
        <span className="flex items-center gap-1.5">
          <input
            type="number"
            className="leo-input w-32"
            value={row.ope_value}
            readOnly={!editable}
            onChange={(e) => onValue(e.target.value)}
          />
          <span className="text-[12px] text-text-muted">元</span>
        </span>
      );
    }
    // 监测链接：URL 长达数百字符，用整宽多行文本域完整展示（等宽字体 +
    // break-all 逐字符换行；行数按内容长度估算——每 90 字符一行、至少 2 行——
    // 保证默认高度即可看完整条链接，并允许用户纵向拖拽再调整）
    if (row.field === FIELD_MONITOR_URL) {
      const rows = Math.max(2, Math.ceil(row.ope_value.length / 90));
      return (
        <textarea
          className="leo-textarea w-full resize-y break-all font-mono"
          rows={rows}
          spellCheck={false}
          value={row.ope_value}
          readOnly={!editable}
          onChange={(e) => onValue(e.target.value)}
        />
      );
    }
    // 其余未知行兜底
    return (
      <input
        type="text"
        className="leo-input w-48"
        value={row.ope_value}
        readOnly={!editable}
        onChange={(e) => onValue(e.target.value)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-[13px] text-text-muted">加载中…</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
            红线规则配置
          </h1>
          <p className="mt-1 text-[12.5px] text-text-muted">
            华为应用商店红线规则（投放时间 / 出价 / 监测链接 / 回传异常监控），保存到
            auto_huawei_store_redline_config；create_user 保留首次、modify_user 每次更新。
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loaded && loaded.length > 0 ? (
            <span className="leo-tag is-info">已配置 · {loaded.length} 行</span>
          ) : (
            <span className="leo-tag is-warning">尚未配置</span>
          )}
          {dirty ? <span className="leo-tag is-warning">有未保存的修改</span> : null}
        </div>
      </div>

      {!canEditPage ? (
        <div className="mb-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
          当前账号为只读，无法编辑该页面配置。
        </div>
      ) : null}

      {items.length === 0 ? (
        <div className="leo-card rounded-xl p-8 text-center">
          <div className="mb-3 text-[12.5px] text-text-muted">
            尚无华为商店红线规则配置，可一键初始化 20 行初版配置（投放时间 / 出价 /
            监测链接 / 回传异常监控）。
          </div>
          <button
            type="button"
            onClick={onSeed}
            disabled={seeding || !canEditPage}
            className="leo-cta-primary w-auto px-7"
          >
            {seeding ? "初始化中..." : "初始化默认配置"}
          </button>
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[12.5px] text-text-muted">
              共 <span className="text-text-emphasis">{items.length}</span> 行配置，主键
              field + parent_id + child_id 只读，仅 ope_value 可编辑
            </div>
          </div>

          <div className="space-y-4">
            {groups.map((group) => (
              <div
                key={group.field}
                className="rounded-xl border border-line-strong bg-da-card p-5 shadow-card"
              >
                <div className="mb-4 flex items-center justify-between gap-2">
                  <div className="text-[15.2px] font-bold text-text-emphasis">
                    {group.field}
                  </div>
                  {group.latest?.modify_user ? (
                    <span className="leo-tag">
                      最近修改：{group.latest.modify_user} · {group.latest.updated_time ?? "—"}
                    </span>
                  ) : null}
                </div>

                {group.parents.map((parent) => {
                  const wide = parent.rows.some((r) => isWideEditor(r.row));
                  return (
                    <div key={parent.parentId} className="mb-4 last:mb-0">
                      <div className="mb-2 text-[12.5px] font-semibold text-text-strong">
                        {parent.parentId}
                      </div>
                      {wide ? (
                        <div className="space-y-3">
                          {parent.rows.map(({ row, index }) => (
                            <div
                              key={row.child_id}
                              className="flex flex-col gap-1.5 sm:flex-row sm:items-start"
                            >
                              <span className="whitespace-nowrap pt-1 text-[12px] text-text-muted sm:w-24">
                                {row.child_id}
                              </span>
                              <div className="min-w-0 flex-1">
                                {renderEditor(row, index, canEditPage)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
                          {parent.rows.map(({ row, index }) => (
                            <label key={row.child_id} className="flex items-center gap-2">
                              <span className="whitespace-nowrap text-[12px] text-text-muted">
                                {row.child_id}
                              </span>
                              {renderEditor(row, index, canEditPage)}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="mt-7 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => reload()}
              disabled={saving}
              className="leo-btn-ghost"
            >
              重新加载
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || !canEditPage}
              className="leo-cta-primary w-auto px-7"
            >
              {saving ? "保存中..." : dirty ? "保存配置" : "已保存"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
